import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Check, X, Minus, Ship, Calendar, Sparkles, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { boatImage } from "../lib/boatImages";
import WelcomeBanner from "../components/WelcomeBanner";
import { useAuth } from "../lib/auth";

const SHORT = {
  basic_cleaning: "Rengjøring",
  bed_linen: "Sengetøy",
  basics_complete: "Basisvarer",
  septic_emptied: "Septik tømt",
  water_full: "Vanntank full",
  surfaces: "Overflater",
  exterior_wash: "Utvendig vask",
  trash: "Søppel",
  charge_checked: "Lading sjekket",
};

function StatusPill({ status }) {
  if (status === "ok") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#1DA3B3]/15 text-[#1DA3B3] text-[11px] font-semibold">
        <Check className="w-3 h-3" /> OK
      </span>
    );
  }
  if (status === "not_ok") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[11px] font-semibold">
        <X className="w-3 h-3" /> Avvik
      </span>
    );
  }
  if (status === "not_needed") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#BD984D]/15 text-[#A57F39] text-[11px] font-semibold">
        <Minus className="w-3 h-3" /> Ikke nødvendig
      </span>
    );
  }
  if (status === "na") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">
        <Minus className="w-3 h-3" /> N/A
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#BD984D]/15 text-[#A57F39] text-[11px] font-semibold">
      Ikke gjort
    </span>
  );
}

export default function VaskeplanPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ boats: [], summary: [] });
  const [busy, setBusy] = useState({});
  const [myRecent, setMyRecent] = useState([]);
  const [recentDismissed, setRecentDismissed] = useState(
    () => sessionStorage.getItem("cbt:vask_recent_dismissed") === "1"
  );
  // Vask uses /admin/boats/:id like other worker roles (the route already permits vask).
  const boatBase = "/admin";
  const defectsBase =
    user?.role === "service" ? "/service" : user?.role === "vask" ? "/vask" : "/admin";

  const load = async () => {
    const { data: r } = await api.get("/vaske-plan");
    setData(r);
    try {
      const { data: rec } = await api.get("/defects/my-recent", { params: { hours: 24 } });
      setMyRecent(rec);
    } catch { /* non-blocking */ }
  };

  const dismissRecent = () => {
    sessionStorage.setItem("cbt:vask_recent_dismissed", "1");
    setRecentDismissed(true);
  };

  useEffect(() => { load(); }, []);

  const setItem = async (boatId, key, status) => {
    const id = `${boatId}:${key}`;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const payload = status === null
        ? { key, clear: true }
        : { key, status };
      const res = await api.patch(`/vaske-plan/${boatId}/item`, payload);
      if (res.data.completed) toast.success("Hele båten er klar – sjekkliste fullført ✨");
      await load();
    } catch {
      toast.error("Kunne ikke oppdatere");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const aggregate = data.summary.filter((s) => s.boats_needing > 0);

  return (
    <div className="space-y-6">
      <WelcomeBanner />

      {/* Recent defects banner – immediate feedback that vask's reports went through to service/admin */}
      {!recentDismissed && myRecent.length > 0 && (
        <Card
          className="overflow-hidden border-l-4 border-l-[#BD984D] border-[#BD984D]/30 bg-[#BD984D]/5"
          data-testid="vask-recent-defects-banner"
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#A57F39] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#0C1824]">
                    Du har rapportert {myRecent.length}{" "}
                    {myRecent.length === 1 ? "avvik" : "avvik"} siste 24 timer
                  </div>
                  <p className="text-sm text-[#2B3F4D] mt-0.5">
                    Service og admin er varslet. Trykk for å åpne saken.
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissRecent}
                data-testid="vask-recent-dismiss"
                className="text-[#2B3F4D] hover:bg-[#0C1824]/5"
              >
                Skjul
              </Button>
            </div>
            <div className="mt-3 space-y-1.5">
              {myRecent.slice(0, 5).map((d) => {
                const boat = data.boats.find((bb) => bb.boat.id === d.boat_id);
                return (
                  <Link
                    key={d.id}
                    to={`${defectsBase}/defects?focus=${d.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-white/70 hover:bg-white transition-colors border border-[#BD984D]/20"
                    data-testid={`vask-recent-defect-${d.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Ship className="w-3.5 h-3.5 text-[#A57F39] flex-shrink-0" />
                      <span className="text-xs uppercase tracking-[0.18em] text-[#A57F39] flex-shrink-0">
                        {boat?.boat.name || "Båt"}
                      </span>
                      <span className="text-sm font-medium text-[#0C1824] truncate">
                        {d.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge
                        className={
                          d.status === "resolved"
                            ? "bg-[#1DA3B3]/15 text-[#1DA3B3]"
                            : d.status === "in_progress"
                            ? "bg-[#D4B060]/20 text-[#A57F39]"
                            : "bg-red-100 text-red-700"
                        }
                      >
                        {d.status === "resolved"
                          ? "Løst"
                          : d.status === "in_progress"
                          ? "Under arbeid"
                          : "Åpen"}
                      </Badge>
                      <ChevronRight className="w-3.5 h-3.5 text-[#2B3F4D]" />
                    </div>
                  </Link>
                );
              })}
              {myRecent.length > 5 && (
                <div className="text-xs text-[#2B3F4D] pl-3 pt-1">
                  + {myRecent.length - 5} flere – se{" "}
                  <Link
                    to={`${defectsBase}/defects`}
                    className="text-[#A57F39] font-semibold hover:underline"
                  >
                    Avvik-listen
                  </Link>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Renhold</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Vaskeplan</h1>
        <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Alle båter samlet på ett sted. Se hva som mangler før du drar ut – marker punkter som ferdige
          underveis. Når alle punktene på en båt er OK, fullføres sjekklisten automatisk.
        </p>
      </div>

      {/* Aggregert ta-med-liste */}
      <Card className="overflow-hidden border-2 border-[#BD984D]/40">
        <div className="bg-[#0C1824] text-white px-5 py-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#ECC974]" />
          <div className="text-xs uppercase tracking-[0.32em] text-[#ECC974]">
            Plan før utkjøring
          </div>
        </div>
        <div className="p-5">
          {aggregate.length === 0 ? (
            <div className="text-sm text-[#2B3F4D]">
              ✓ Alt er gjort på alle båter. Ingenting du trenger å ta med.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {aggregate.map((s) => (
                <div
                  key={s.key}
                  className="rounded-lg border border-[#E2E8F0] p-3"
                  data-testid={`agg-${s.key}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">
                    {SHORT[s.key] || s.label_no}
                  </div>
                  <div className="text-2xl font-bold text-[#0C1824] mt-1">
                    {s.boats_needing}
                    <span className="text-sm font-normal text-[#2B3F4D] ml-1.5">
                      {s.boats_needing === 1 ? "båt" : "båter"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Per båt */}
      <div className="space-y-4">
        {data.boats.map(({ boat, items, pending_count, last_done_at, last_done_by, next_booking }) => (
          <Card key={boat.id} className="overflow-hidden" data-testid={`vask-boat-${boat.id}`}>
            <div className="grid md:grid-cols-[200px_1fr]">
              <div className="relative h-32 md:h-auto bg-slate-100">
                <img
                  src={boatImage(boat)}
                  alt={boat.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C1824]/70 to-transparent" />
                <div className="absolute bottom-2 left-3 text-white">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
                    {boat.model} · {boat.length_ft}&apos;
                  </div>
                  <div className="text-lg font-bold leading-tight drop-shadow flex items-center gap-1.5">
                    <Ship className="w-4 h-4" />
                    {boat.name}
                  </div>
                </div>
                <Badge
                  className={`absolute top-2 right-2 ${
                    boat.status === "ute"
                      ? "bg-[#BD984D]/95 text-[#0C1824]"
                      : "bg-[#1DA3B3]/95 text-white"
                  }`}
                >
                  {boat.status === "ute" ? "Ute" : "Inne"}
                </Badge>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="text-sm text-[#2B3F4D]">
                    {pending_count === 0 ? (
                      <span className="text-[#1DA3B3] font-semibold">✓ Ferdig vasket</span>
                    ) : (
                      <span>
                        <span className="text-red-600 font-bold">{pending_count}</span> punkt(er) gjenstår
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#2B3F4D]">
                    {last_done_at && (
                      <div>
                        Sist vasket:{" "}
                        <span className="font-medium text-[#0C1824]">
                          {new Date(last_done_at).toLocaleDateString("nb-NO")}
                        </span>
                        {last_done_by && <> · {last_done_by}</>}
                      </div>
                    )}
                    {next_booking && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-[#A57F39]" />
                        Neste innsjekk:{" "}
                        <span className="font-medium text-[#0C1824]">
                          {next_booking.start_date?.slice(0, 10)}
                        </span>
                      </div>
                    )}
                    <Link to={`${boatBase}/boats/${boat.id}`}>
                      <Button
                        variant="outline"
                        className="h-7 px-2.5 text-[11px] border-[#0C1824]/30 text-[#0C1824] hover:bg-[#0C1824]/5"
                        data-testid={`vask-open-boat-${boat.id}`}
                        title="Inventar, flytevester og rapporter avvik"
                      >
                        Åpne båt
                        <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {boat.status === "ute" && (
                    <div
                      className="sm:col-span-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#BD984D]/10 border border-[#BD984D]/30 text-sm text-[#A57F39]"
                      data-testid={`vask-locked-${boat.id}`}
                    >
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span>
                        <strong>{boat.name}</strong> er ute på tur. Renhold kan først gjøres når
                        båten kommer tilbake – da opprettes utsjekk- og renhold-sjekkliste
                        automatisk.
                      </span>
                    </div>
                  )}
                  {items.map((it) => {
                    const id = `${boat.id}:${it.key}`;
                    const isOk = it.status === "ok";
                    const isNotNeeded = it.status === "not_needed";
                    const isDone = isOk || isNotNeeded;
                    const locked = boat.status === "ute";
                    return (
                      <div
                        key={it.key}
                        className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 px-3 py-2.5 rounded-lg border ${
                          locked
                            ? "bg-slate-50 border-slate-200 opacity-60"
                            : isOk
                            ? "bg-[#1DA3B3]/5 border-[#1DA3B3]/20"
                            : isNotNeeded
                            ? "bg-[#BD984D]/5 border-[#BD984D]/20"
                            : "bg-white border-[#E2E8F0]"
                        }`}
                        data-testid={`vask-item-${boat.id}-${it.key}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm ${isDone ? "text-[#2B3F4D] line-through" : "text-[#0C1824] font-medium"}`}>
                            {it.label_no}
                          </div>
                          {it.last_updated_by && (
                            <div className="text-[10px] text-[#2B3F4D] mt-0.5">
                              {it.last_updated_by} ·{" "}
                              {it.last_updated_at &&
                                new Date(it.last_updated_at).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                          {/* Pill only adds value when item IS done – when undone the action
                              buttons already communicate the state and the pill would just
                              overlap the wrapped label on narrow screens. */}
                          {isDone && <StatusPill status={it.status} />}
                          {isDone ? (
                            <Button
                              size="sm"
                              disabled={busy[id] || locked}
                              variant="outline"
                              onClick={() => setItem(boat.id, it.key, null)}
                              data-testid={`vask-undo-${boat.id}-${it.key}`}
                              className="border-slate-300 text-slate-600 hover:bg-slate-50 h-8 px-2.5"
                            >
                              Angre
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                disabled={busy[id] || locked}
                                onClick={() => setItem(boat.id, it.key, "ok")}
                                data-testid={`vask-toggle-${boat.id}-${it.key}`}
                                className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white h-8 px-3"
                              >
                                <Check className="w-3.5 h-3.5 lg:mr-1" />
                                <span className="hidden lg:inline">Marker ferdig</span>
                                <span className="lg:hidden">Ferdig</span>
                              </Button>
                              <Button
                                size="sm"
                                disabled={busy[id] || locked}
                                variant="outline"
                                onClick={() => setItem(boat.id, it.key, "not_needed")}
                                data-testid={`vask-not-needed-${boat.id}-${it.key}`}
                                className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10 h-8 px-2.5"
                                title="Ikke nødvendig (f.eks. sengetøy ved dagsturer)"
                              >
                                <Minus className="w-3.5 h-3.5 lg:mr-1" />
                                <span className="hidden lg:inline">Ikke nødvendig</span>
                                <span className="lg:hidden">Ikke nødv.</span>
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
